import { useEffect } from 'react';

/**
 * Keeps the focused text input above the on-screen keyboard.
 *
 * Android shrinks the layout viewport when the keyboard opens, so the browser
 * reflows/scrolls the focused input into view for free. iOS Safari does NOT
 * resize the layout viewport — the keyboard overlays the content and only
 * `window.visualViewport` shrinks — and its native focus-scroll can't lift
 * inputs that live in fixed overlays or fixed-height nested scroll containers
 * (which is every typing surface in the calendar). This hook fills that gap.
 *
 * On focus and on visualViewport resize, if the focused input's bottom is below
 * the keyboard's top edge, it scrolls the input's nearest scrollable ancestor
 * (or the window) up by the overlap. When the scroller doesn't have that much
 * scroll room left (the last items in a list), it temporarily pads the
 * scroller's bottom to make the room — mirroring Android, where the whole list
 * moves clear of the keyboard — and removes the padding once the keyboard
 * closes or focus leaves text entry.
 *
 * Opt-in: an input with `data-keyboard-lift="form"` lifts its whole enclosing
 * <form> above the keyboard instead of just itself (e.g. the Sets field, so the
 * per-set rows below it are visible too), without scrolling the input itself
 * off the top when the form is taller than the visible area.
 *
 * It is capability-agnostic: on desktop and on Android the keyboard doesn't
 * cover the visual viewport (or the platform already scrolled), so the overlap
 * is <= 0 and it no-ops — no scroll, no padding.
 *
 * Mount once (e.g. at a module root); it installs document-level listeners, so
 * it also covers inputs in portaled modals/overlays.
 */

const BOTTOM_MARGIN = 16;

const isTextEntry = (el) => {
  if (!el) return false;
  const { tagName } = el;
  if (tagName === 'TEXTAREA') return true;
  if (tagName === 'INPUT') {
    const type = (el.getAttribute('type') || 'text').toLowerCase();
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(type);
  }
  return el.isContentEditable;
};

/** Whether `el` can be scrolled: it already overflows, or its height is
 * constrained so padding it would create scroll room. A content-sized box
 * (e.g. one that only computes to overflow-y: auto because overflow-x is
 * hidden, like the calendar's LineContainer) grows with its content instead,
 * so scrolling it is a no-op and padding it just makes it taller. Computed
 * styles can't tell the two apart, so probe with 1px of padding. */
const canScroll = (el) => {
  if (el.scrollHeight > el.clientHeight) return true;
  const inlineValue = el.style.paddingBottom;
  const basePx = parseFloat(window.getComputedStyle(el).paddingBottom) || 0;
  const heightBefore = el.clientHeight;
  // eslint-disable-next-line no-param-reassign
  el.style.paddingBottom = `${basePx + 1}px`;
  const grew = el.clientHeight > heightBefore;
  // eslint-disable-next-line no-param-reassign
  el.style.paddingBottom = inlineValue;
  return !grew;
};

/** Nearest overflow-y: auto|scroll ancestor that can actually scroll — whether
 * or not it overflows yet, a short fixed-height list that doesn't scroll today
 * can still be padded to scroll. Content-sized overflow boxes are skipped. */
const nearestScrollableAncestor = (el) => {
  let node = el.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && canScroll(node)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

/** The rect to keep above the keyboard, and how far it may be lifted before
 * the focused input itself would go off the top of the visible area. */
const getLiftTarget = (el, vv) => {
  const inputRect = el.getBoundingClientRect();
  const form = el.getAttribute('data-keyboard-lift') === 'form' ? el.closest('form') : null;
  if (!form) return { rect: inputRect, maxLift: Infinity };
  return {
    rect: form.getBoundingClientRect(),
    maxLift: inputRect.top - vv.offsetTop - BOTTOM_MARGIN,
  };
};

const useKeyboardAwareInputScroll = () => {
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;

    /** the element we padded, its original inline padding-bottom, its
     * computed padding before we touched it, and how much we've added */
    let padded = null;

    const removePadding = () => {
      if (!padded) return;
      padded.el.style.paddingBottom = padded.inlineValue;
      padded = null;
    };

    /** Grow `el`'s bottom padding by `shortfall` px so it can scroll that much
     * further. */
    const addPadding = (el, shortfall) => {
      if (padded && padded.el !== el) removePadding();
      if (!padded) {
        padded = {
          el,
          inlineValue: el.style.paddingBottom,
          basePx: parseFloat(window.getComputedStyle(el).paddingBottom) || 0,
          extra: 0,
        };
      }
      padded.extra += shortfall;
      // eslint-disable-next-line no-param-reassign
      el.style.paddingBottom = `${padded.basePx + padded.extra}px`;
    };

    /** scroll `el` by `delta` px and return how far it actually moved */
    const scrollBy = (el, delta) => {
      const before = el.scrollTop;
      // eslint-disable-next-line no-param-reassign
      el.scrollTop += delta;
      return el.scrollTop - before;
    };

    const adjust = () => {
      /** how much of the viewport bottom the keyboard is covering */
      const keyboardInset = window.innerHeight - (vv.height + vv.offsetTop);
      if (keyboardInset <= 1) {
        // no keyboard covering (desktop / Android reflow / keyboard closed)
        removePadding();
        return;
      }

      const el = document.activeElement;
      if (!isTextEntry(el)) return;

      const { rect, maxLift } = getLiftTarget(el, vv);
      const visibleBottom = vv.offsetTop + vv.height;
      const overlap = Math.min(rect.bottom - visibleBottom + BOTTOM_MARGIN, maxLift);
      if (overlap <= 0) return; // already sitting above the keyboard

      const scroller = nearestScrollableAncestor(el);
      if (scroller) {
        const room = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
        if (overlap > room) addPadding(scroller, overlap - Math.max(room, 0));
        const moved = scrollBy(scroller, overlap);
        /** scrollHeight can lag the content (virtualized rows re-measure
         * late), so the scroll may clamp short of the estimate: pad the
         * measured shortfall and finish the lift in this pass */
        if (moved < overlap) {
          addPadding(scroller, overlap - moved);
          scrollBy(scroller, overlap - moved);
        }
      } else {
        const page = document.scrollingElement || document.documentElement;
        const room = page.scrollHeight - window.innerHeight - window.scrollY;
        if (overlap > room) addPadding(document.body, overlap - Math.max(room, 0));
        window.scrollBy(0, overlap);
      }
    };

    let focusTimer;
    let blurTimer;
    const onFocusIn = (e) => {
      if (!isTextEntry(e.target)) return;
      clearTimeout(blurTimer);
      /** wait for the keyboard to animate in / the viewport to settle; the
       * visualViewport resize below handles the keyboard-opening moment, this
       * covers focusing another field while the keyboard is already up */
      clearTimeout(focusTimer);
      focusTimer = setTimeout(adjust, 300);
    };

    const onFocusOut = () => {
      /** defer so field-to-field moves (focusin lands right after) keep the
       * padding instead of collapsing and re-growing it */
      clearTimeout(blurTimer);
      blurTimer = setTimeout(() => {
        if (!isTextEntry(document.activeElement)) removePadding();
      }, 0);
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    vv.addEventListener('resize', adjust);

    return () => {
      clearTimeout(focusTimer);
      clearTimeout(blurTimer);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      vv.removeEventListener('resize', adjust);
      removePadding();
    };
  }, []);
};

export default useKeyboardAwareInputScroll;
